import { type Point, point } from '../../../elements';
import {
  ICON_DEFAULT_ACCENT_COLOR,
  ICON_DEFAULT_FOREGROUND_COLOR,
} from '../../../graphConstants';
import { makeTag } from '../../../svg/utils';
import {
  type NodeIconOptions,
  type NodeTypeIcon,
  nodeIconDefaultOptions,
} from '../NodeTypeIcon';
import {
  createSquareNodeTypeIcon,
  getArrowSvgPath,
  wrapNodeIconInSvgTag,
} from '../common';
import { drawSquareBackground } from '../drawIconBackground';

const ICON_SIZE = 40;

const BRICK_SIZE = 13.33;
const BRICK_GAP = 2.5;
const BRICK_ROW_GAP = 2;
const BRICK_HEIGHT = 4;
const BRICK_STEP = BRICK_SIZE + BRICK_GAP;
const BRICKS_START_AT = 20 + BRICK_GAP;

const ARROWS_TOP = 6;
const ARROWS_BOTTOM = 18;
const ARROW_LEFT = 12.8;
const ARROW_RIGHT = 27.2;

const START_CENTER_BRICK =
  -BRICK_STEP + (((ICON_SIZE - BRICK_SIZE) / 2) % BRICK_STEP);
const START_CENTER_GAP =
  -BRICK_STEP + (((ICON_SIZE - BRICK_GAP) / 2) % BRICK_STEP) + BRICK_GAP;

function createFwIcon(options: NodeIconOptions = {}): string {
  const opts = { ...nodeIconDefaultOptions, ...options };

  const drawArrow = (from: Point, to: Point) =>
    makeTag('path', {
      d: getArrowSvgPath({
        from,
        to,
        headWidth: 10,
        headHeight: 5.2,
        tailWidth: 3.6,
      }),
      fill: ICON_DEFAULT_FOREGROUND_COLOR,
    });

  const drawBrick = (x: number, y: number, width = BRICK_SIZE) =>
    makeTag('rect', {
      x,
      y,
      width,
      height: BRICK_HEIGHT,
      fill: ICON_DEFAULT_ACCENT_COLOR,
    });

  const drawBrickRow = (startX: number, y: number) => {
    let result = '';
    for (let x = startX; x < ICON_SIZE; x += BRICK_STEP) {
      result += drawBrick(
        Math.max(x, 0),
        y,
        Math.min(BRICK_SIZE, ICON_SIZE - x) + Math.min(x, 0),
      );
    }
    return result;
  };

  const getBrickRowY = (brickRow: number) =>
    BRICKS_START_AT + (BRICK_HEIGHT + BRICK_ROW_GAP) * brickRow;

  const icon = `
    ${drawSquareBackground(ICON_SIZE, opts.style)}
    ${drawArrow(point.create(ARROW_LEFT, ARROWS_TOP), point.create(ARROW_LEFT, ARROWS_BOTTOM))}
    ${drawArrow(point.create(ARROW_RIGHT, ARROWS_BOTTOM), point.create(ARROW_RIGHT, ARROWS_TOP))}
    ${drawBrickRow(START_CENTER_GAP, getBrickRowY(0))}
    ${drawBrickRow(START_CENTER_BRICK, getBrickRowY(1))}
    ${drawBrickRow(START_CENTER_GAP, getBrickRowY(2))}
  `;

  return wrapNodeIconInSvgTag(icon, ICON_SIZE, ICON_SIZE, 'topLeft', opts);
}

export const fwIcon: NodeTypeIcon = createSquareNodeTypeIcon(
  ICON_SIZE,
  createFwIcon,
);
